<template>
    <div class="contact-page">
        <GallabLogo class="logo" />

        <div class="header">
            <h1>Kontakt</h1>
            <button class="select-button" @click="goToConfigurator">
                Przejdź do konfiguratora
            </button>
        </div>

        <div v-if="hasAnyContactText" class="contact-columns">
            <div class="two-columns">
                <div class="column" v-if="leftColumnHtml" v-html="leftColumnHtml"></div>

                <div class="column" v-if="rightColumn" v-html="rightColumn.content"></div>
            </div>

            <div class="column" v-if="socialLinks.length">
                <div v-if="socialColumn" v-html="cleanSocialText"></div>
                <div class="social-icons">
                    <a v-for="(link, index) in socialLinks" :key="index" :href="link.url" target="_blank"
                        rel="noopener noreferrer" class="social-link">
                        <img :src="link.icon" :alt="link.name" class="social-img" />
                    </a>
                </div>
            </div>
        </div>

        <section v-else>
            <p>Brak treści</p>
        </section>
    </div>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useTextsStore } from '@/stores/textsStore'
import GallabLogo from '@/components/common/GallabLogo.vue'

import FacebookLogo from '@/assets/social-logos/Facebook_logo.png'
import InstagramLogo from '@/assets/social-logos/Instagram_logo.png'
import WhatsAppLogo from '@/assets/social-logos/WhatsApp_logo.png'

export default {
    name: 'ContactPage',
    components: { GallabLogo },
    methods: {
        goToConfigurator() {
            this.$router.push('/')
        },
    },
    setup() {
        const textsStore = useTextsStore()

        onMounted(() => {
            textsStore.fetchTexts()
        })

        const leftColumn = computed(() =>
            textsStore.texts.find(t => t.name === 'Kontakt - lewa kolumna')
        )
        const rightColumn = computed(() =>
            textsStore.texts.find(t => t.name === 'Kontakt - prawa kolumna')
        )
        const socialColumn = computed(() =>
            textsStore.texts.find(t => t.name === 'Kontakt - social media')
        )

        const hasAnyContactText = computed(() =>
            Boolean(leftColumn.value || rightColumn.value || socialColumn.value)
        )

        const processContactColumn = (column) => {
            if (!column?.content) return ''
            let content = column.content

            const phoneRegex = /(\+?\d{2,3}[\s-]?\d{3}[\s-]?\d{3}[\s-]?\d{3,4}|\b\d{9,10}\b)/g
            content = content.replace(phoneRegex, '<a href="tel:$1">$1</a>')

            const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g
            content = content.replace(emailRegex, '<a href="mailto:$1">$1</a>')

            return content
        }

        const leftColumnHtml = computed(() => processContactColumn(leftColumn.value))

        const socialLinks = computed(() => {
            if (!socialColumn.value?.content) return []

            const text = socialColumn.value.content
            const links = []

            const socialMap = [
                { name: 'WhatsApp', domain: 'wa.me', icon: WhatsAppLogo },
                { name: 'Facebook', domain: 'facebook.com', icon: FacebookLogo },
                { name: 'Instagram', domain: 'instagram.com', icon: InstagramLogo },
            ]

            socialMap.forEach(s => {
                const regex = new RegExp(`https?://(?:www\\.)?${s.domain}/[^\\s<]+`, 'g')
                const matches = text.match(regex)
                if (matches) {
                    matches.forEach(url => links.push({ url, name: s.name, icon: s.icon }))
                }
            })

            return links
        })

        const cleanSocialText = computed(() => {
            if (!socialColumn.value?.content) return ''
            let text = socialColumn.value.content
            const linkRegex = /https?:\/\/[^\s<]+/g
            return text.replace(linkRegex, '').trim()
        })

        return {
            leftColumnHtml,
            rightColumn,
            socialColumn,
            hasAnyContactText,
            socialLinks,
            cleanSocialText,
        }
    },
}
</script>


<style scoped lang="scss">
.contact-page {
    max-width: 1000px;
    margin: 0 auto;
    margin-bottom: 100px;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
    line-height: 1.6;
    min-height: 100vh;
    color: $color-primary-dark;

    h1 {
        font-size: 1.7rem;
        margin: 24px 0;
        color: $color-primary;
        width: 400px;
        padding-left: 20px;
        background: $color-primary;
        color: $color-white;
        clip-path: polygon(20px 50%,
                0 0,
                calc(100% - 20px) 0,
                100% 50%,
                calc(100% - 20px) 100%,
                0 100%,
                0 0);
    }

    section p {
        margin-top: 0.5rem;
        font-size: 1rem;
        color: $color-text-light;
        text-align: justify;
    }
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
}

.contact-columns {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    background-color: white;
    padding: 40px;
    border-radius: 8px;
    opacity: 0.8;
    margin-top: 20px;
}

.two-columns {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 2rem;

    @media (max-width: 768px) {
        flex-direction: column;
    }
}

.column {
    flex: 1;

    :deep(p) {
        margin: 0;
    }

    :deep(h4) {
        margin-top: 0;
        margin-bottom: 0;
    }
}


.select-button {
    width: 200px;
    height: 50px;
    padding: 8px 0;
    background-color: transparent;
    border: 1px solid $color-primary;
    border-radius: 4px;
    color: $color-primary;
    font-weight: 500;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.25s ease;

    &:hover {
        background-color: $color-primary;
        color: $color-white;
    }
}

.social-icons {
    display: flex;
    gap: 10px;
    margin-top: 20px;
}

.social-link {
    display: inline-block;
}

.social-img {
    width: 32px;
    height: 32px;
    object-fit: contain;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.1);
    }
}

:deep(a) {
    color: #c3da82;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
    text-decoration: underline;
    
    &:hover {
        color: #8e9b62;
    }
}
</style>
